import { useState } from 'react';
import { createPortal } from 'react-dom';
import { addItems, useList } from '../data/lists';
import { isLidl, useStore } from '../data/stores';
import { BottomSheet } from './BottomSheet';
import { DraftInput } from './draft/DraftInput';
import { DraftPreview } from './draft/DraftPreview';
import { useDraft } from './draft/useDraft';
import { primaryBtn } from './ui/classes';
import { Toast, toast, type ToastMessage } from './ui/Toast';

interface Props {
  listId: string;
  open: boolean;
  onClose: () => void;
}

/** Dopisywanie pozycji do istniejącej listy (np. kolejny SMS „weź jeszcze…”), z tym samym podglądem co nowa lista. */
export function AddItemsSheet({ listId, open, onClose }: Props) {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const done = (count: number) => {
    onClose();
    setMessage(toast(`Dopisano ${count} ${pozycje(count)}.`));
  };
  return createPortal(
    <>
      <BottomSheet open={open} onClose={onClose} title="Dopisz do listy">
        <AddItemsBody listId={listId} onDone={done} />
      </BottomSheet>
      <Toast message={message} onDismiss={() => setMessage(null)} />
    </>,
    document.body,
  );
}

function pozycje(n: number): string {
  if (n === 1) return 'pozycję';
  const d = n % 10;
  const t = n % 100;
  return d >= 2 && d <= 4 && (t < 12 || t > 14) ? 'pozycje' : 'pozycji';
}

function AddItemsBody({ listId, onDone }: { listId: string; onDone: (count: number) => void }) {
  const [text, setText] = useState('');
  const list = useList(listId);
  const store = useStore(list?.storeId);
  const draft = useDraft(text, false, { articles: !store || isLidl(store) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { items, resolutions, pins } = await draft.finalize();
      if (items.length === 0) return;
      await addItems(listId, items, resolutions, pins);
      onDone(items.length);
    } catch {
      setError('Nie udało się dopisać pozycji. Spróbuj jeszcze raz.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <DraftInput
        id="add-items-text"
        label="Co dopisać?"
        value={text}
        onChange={setText}
        autoFocus
        rows={3}
        placeholder={'bułki x6\nsok pomarańczowy'}
      />
      <DraftPreview draft={draft} title="Do dopisania" />
      <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 bg-white px-4 pt-2 dark:bg-stone-900">
        {error && <p className="text-center text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button type="button" onClick={() => void submit()} disabled={busy || draft.items.length === 0} className={primaryBtn}>
          {busy ? 'Dopisuję…' : `Dopisz (${draft.items.length})`}
        </button>
      </div>
    </div>
  );
}
